/**
 * Canvas, panel, and cursor entries for the Figma export showcase.
 *
 * Uses production components directly where possible so the export
 * reflects the real visual output, not stale workshop wrappers.
 */

import { useState } from "react";
import { Icons } from "@/components/ui/icons";
// --- Production components (no store deps — safe in workshop) ---
import { SpaceSwitcher } from "@/components/panels/space-switcher";
import { AgentDock } from "@/components/canvas/agent-dock";
import { LeftSidebar } from "@/components/panels/left-sidebar";
import type { FrameEntry, TaskEntry } from "@/components/panels/left-sidebar";
import type { BrowseProject } from "@/components/panels/browse-tree";
import type { ExecutionSidebarProps } from "@/components/panels/execution-sidebar";
// --- Workshop wrappers for store-bound components ---
import { AgentAvatar } from "@/workshop/components/canvas/agent-avatar";
import { AgentCursor } from "@/workshop/components/cursors/agent-cursor";
import { VoiceListeningGlass } from "@/components/cursors/voice-listening-glass";
import { RightPanel } from "@/workshop/components/panels/right-panel";
import { InputBar } from "@/workshop/components/panels/input-bar";
import { ExecutionStatusBar } from "@/workshop/components/panels/execution-status-bar";
import { MOCK_EXECUTION_TODOS } from "@/workshop/data/mock-workflows";
import type { ExportGroup } from "./export-types";

// --- Mock data ---

const MOCK_PROJECTS = [
  {
    id: "p1",
    name: "Denker Canvas",
    color: "#818CF8",
    active: true,
    spaces: [
      { id: "s1", label: "Main", active: true },
      { id: "s2", label: "Research" },
      { id: "s3", label: "Drafts" },
    ],
  },
  {
    id: "p2",
    name: "Side Project",
    color: "#34D399",
    spaces: [
      { id: "s4", label: "Planning" },
      { id: "s5", label: "Notes" },
    ],
  },
  {
    id: "p3",
    name: "Archive",
    color: "#F472B6",
    spaces: [{ id: "s6", label: "Old work" }],
  },
];

const MOCK_DOCK_AGENTS = [
  {
    id: "a1",
    letter: "C",
    avatarSeed: "claude",
    statusColor: "bg-accent",
    color: "#818CF8",
  },
  {
    id: "a2",
    letter: "R",
    avatarSeed: "researcher",
    statusColor: "bg-warning",
    color: "#F472B6",
  },
  {
    id: "a3",
    letter: "W",
    avatarSeed: "writer",
    statusColor: "bg-muted",
    color: "#34D399",
  },
];

const MOCK_FRAMES: FrameEntry[] = [
  {
    id: "f1",
    title: "API Integration Notes",
    icon: Icons.FileText,
    active: true,
    statusColor: "bg-accent",
  },
  {
    id: "f2",
    title: "Search: React patterns",
    icon: Icons.Search,
    statusColor: "bg-accent",
  },
  { id: "f3", title: "main.py", icon: Icons.Code, statusColor: "bg-muted" },
  {
    id: "f4",
    title: "Architecture diagram",
    icon: Icons.Image,
    statusColor: "bg-warning",
  },
  {
    id: "f5",
    title: "Deployment checklist",
    icon: Icons.FileText,
    hidden: true,
  },
];

const MOCK_BROWSE_PROJECTS: BrowseProject[] = [
  {
    id: "p1",
    name: "Denker Canvas",
    color: "#818CF8",
    isPinned: true,
    expanded: true,
    spaces: [
      { id: "s1", name: "Main", active: true },
      { id: "s2", name: "Research" },
      { id: "s3", name: "Drafts" },
    ],
  },
  {
    id: "p2",
    name: "Side Project",
    color: "#34D399",
    expanded: false,
    spaces: [
      { id: "s4", name: "Planning" },
      { id: "s5", name: "Notes" },
    ],
  },
  {
    id: "p3",
    name: "Archive",
    color: "#F472B6",
    expanded: false,
    spaces: [{ id: "s6", name: "Old work" }],
  },
];

const MOCK_TASKS: TaskEntry[] = [
  {
    id: "task1",
    title: "Implement auth flow",
    checked: false,
    agentLetter: "C",
    agentColor: "#818CF8",
  },
  {
    id: "task2",
    title: "Write unit tests",
    checked: true,
    agentLetter: "R",
    agentColor: "#F472B6",
  },
  {
    id: "task3",
    title: "Review PR #42",
    checked: false,
    agentLetter: "W",
    agentColor: "#34D399",
  },
];

const MOCK_DETAIL_SECTIONS = [
  {
    title: "Frame Info",
    rows: [
      { label: "Type", value: "Code Editor" },
      { label: "Created", value: "2026-03-10 14:32" },
      { label: "Last modified", value: "2 min ago" },
      { label: "Agent", value: "Coder" },
    ],
  },
  {
    title: "Properties",
    rows: [
      { label: "Language", value: "Python" },
      { label: "Lines", value: "247" },
      { label: "Status", value: "Active" },
    ],
  },
];

const MOCK_INPUT_AGENTS = [
  { id: "a1", name: "Coder", color: "#30D158", avatar_seed: "coder" },
  { id: "a2", name: "Researcher", color: "#60A5FA", avatar_seed: "researcher" },
  { id: "a3", name: "Writer", color: "#818CF8", avatar_seed: "writer" },
];

// --- Stateful demo wrappers ---

function SpaceSwitcherDemo() {
  const [projects, setProjects] = useState(MOCK_PROJECTS);
  return (
    <SpaceSwitcher
      projects={projects}
      onSelectProject={(pid) =>
        setProjects((prev) => prev.map((p) => ({ ...p, active: p.id === pid })))
      }
      onSelectSpace={() => {}}
      onAddProject={() => {}}
      onAddSpace={() => {}}
    />
  );
}

function CompactSpaceSwitcherDemo() {
  const [projects, setProjects] = useState(MOCK_PROJECTS);
  return (
    <SpaceSwitcher
      projects={projects}
      density="compact"
      onSelectProject={(pid) =>
        setProjects((prev) => prev.map((p) => ({ ...p, active: p.id === pid })))
      }
      onSelectSpace={() => {}}
    />
  );
}

function LeftSidebarDemo() {
  const [tab, setTab] = useState<"frames" | "browse">("frames");
  const execution: ExecutionSidebarProps = {
    executionState: "running",
    todos: MOCK_EXECUTION_TODOS,
    onStop: () => {},
  };
  return (
    <div className="h-[600px] w-60 overflow-hidden rounded-lg border border-glass-stroke">
      <LeftSidebar
        showHeader
        projectName="Denker Canvas"
        spaceName="Main"
        activeTab={tab}
        frames={MOCK_FRAMES}
        tasks={MOCK_TASKS}
        browseProjects={MOCK_BROWSE_PROJECTS}
        onTabChange={setTab}
        onAddFrame={() => {}}
        onSelectFrame={() => {}}
        onToggleFrameVisibility={() => {}}
        onBrowseSelectProject={() => {}}
        onBrowseSelectSpace={() => {}}
        onBrowseToggleProject={() => {}}
        onBrowseNewProject={() => {}}
        onBrowseNewSpace={() => {}}
        execution={execution}
      />
    </div>
  );
}

function RightPanelDemo() {
  const [tab, setTab] = useState<"details" | "logs" | "actions">("details");
  return (
    <div className="h-[560px] overflow-hidden rounded-lg border border-glass-stroke">
      <RightPanel
        title="Code Frame"
        activeTab={tab}
        sections={MOCK_DETAIL_SECTIONS}
        onTabChange={setTab}
        onClose={() => {}}
        onEdit={() => {}}
        onDelete={() => {}}
      />
    </div>
  );
}

function InputBarDemo() {
  const [value, setValue] = useState("");
  return (
    <div className="w-[560px] max-w-full">
      <InputBar
        value={value}
        onChange={setValue}
        onSubmit={() => setValue("")}
        agents={MOCK_INPUT_AGENTS}
        placeholder="Ask your agents anything..."
      />
    </div>
  );
}

function AgentAvatarsDemo() {
  return (
    <div className="flex items-end gap-5">
      <AgentAvatar letter="C" size="sm" color="#818CF8" />
      <AgentAvatar
        letter="R"
        size="default"
        color="#F472B6"
        statusColor="bg-warning"
      />
      <AgentAvatar
        letter="W"
        size="lg"
        color="#34D399"
        statusColor="bg-muted"
      />
      <AgentAvatar letter="A" size="default" hideStatus />
    </div>
  );
}

function AgentCursorDemo() {
  return (
    <div className="flex flex-col gap-6">
      <AgentCursor agentName="Writer" agentColor="#34D399" />
      <AgentCursor agentName="Coder" agentColor="#818CF8" thinking />
      <AgentCursor
        agentName="Coder"
        agentColor="#818CF8"
        toolCall="edit_file"
      />
      <AgentCursor
        agentName="Researcher"
        agentColor="#F472B6"
        content="Searching for React state management patterns in large codebases"
      />
    </div>
  );
}

function VoiceIndicatorDemo() {
  const themes: Array<{
    label: string;
    theme: "dark" | "light";
    light: boolean;
  }> = [
    { label: "Dark", theme: "dark", light: false },
    { label: "Light", theme: "light", light: true },
  ];
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {themes.map((t) => (
        <div
          key={t.theme}
          className={
            t.light
              ? "rounded-xl border border-black/10 bg-[#F8FAFC] p-5"
              : "rounded-xl border border-white/[0.08] bg-black/35 p-5"
          }
        >
          <div
            className={
              t.light
                ? "mb-6 text-xs text-black/55"
                : "mb-6 text-xs text-white/45"
            }
          >
            {t.label}
          </div>
          <div className="flex justify-center">
            <VoiceListeningGlass
              color="#30D158"
              volume={0.56}
              camera
              agentName="Denker"
              showAgentName
              surface={t.light ? "light" : "dark"}
              theme={t.theme}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Groups ---

export const CHROME_GROUPS: ExportGroup[] = [
  {
    category: "Canvas",
    entries: [
      {
        id: "space-switcher",
        label: "Space Switcher",
        description:
          "Regular density — project avatars stacked, active project's spaces shown inline.",
        render: () => <SpaceSwitcherDemo />,
      },
      {
        id: "space-switcher-compact",
        label: "Space Switcher (Compact)",
        description: "Compact density — tighter spacing for narrower sidebars.",
        render: () => <CompactSpaceSwitcherDemo />,
      },
      {
        id: "agent-dock",
        label: "Agent Avatar Dock",
        description:
          "Collapsed dock showing Dicebear avatars. Hover card is store-bound in production.",
        render: () => (
          <AgentDock agents={MOCK_DOCK_AGENTS} onAddAgent={() => {}} />
        ),
      },
      {
        id: "agent-avatars",
        label: "Agent Avatars",
        render: () => <AgentAvatarsDemo />,
      },
      {
        id: "session-indicator",
        label: "Session Indicator",
        description:
          "Thin status bar shown between canvas and input bar when agents are running.",
        render: () => <ExecutionStatusBar runningCount={2} />,
      },
    ],
  },
  {
    category: "Panels",
    entries: [
      {
        id: "left-rail",
        label: "Left Rail",
        description:
          "240px sidebar — Frames / Browse tabs, task list, execution progress, desktop header.",
        render: () => <LeftSidebarDemo />,
      },
      {
        id: "right-panel",
        label: "Right Panel",
        render: () => <RightPanelDemo />,
      },
      { id: "input-bar", label: "Input Bar", render: () => <InputBarDemo /> },
    ],
  },
  {
    category: "Cursors & Voice",
    entries: [
      {
        id: "agent-cursor",
        label: "Agent Cursor Bubble",
        render: () => <AgentCursorDemo />,
      },
      {
        id: "voice-indicator",
        label: "Voice Indicator",
        render: () => <VoiceIndicatorDemo />,
      },
    ],
  },
];
